/**
 * Fiche site (CDC §6.2).
 *
 * Elle répond à la question qu'on se pose avant de partir en intervention :
 * « comment j'entre, quand, et à quoi je fais attention ? ». Les consignes
 * d'accès, d'horaires et de sécurité sont donc placées EN TÊTE, avant
 * l'adresse — un technicien refoulé à la barrière a perdu sa demi-journée, qu'il
 * ait eu l'adresse exacte ou non.
 *
 * Le lien `/sites/:id` existait depuis la fiche machine mais ne menait nulle
 * part : c'est ce qui a motivé cet écran.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError, api, type Asset, type Site } from '../api/client';
import { FormulaireSite } from '../composants/FormulaireSite';
import { PiecesJointes } from '../composants/PiecesJointes';
import { STATUT_MACHINE } from '../domaine/machines';

export function SitePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [site, setSite] = useState<Site | null>(null);
  const [machines, setMachines] = useState<Asset[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);

  const charger = useCallback(async () => {
    if (!id) return;
    try {
      const [s, m] = await Promise.all([api.site(id), api.assets({ siteId: id, take: 500 })]);
      setSite(s);
      setMachines([...m.items]);
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Chargement impossible.');
    }
  }, [id]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const archiver = async () => {
    if (!site) return;
    const motif = window.prompt(
      `Archiver « ${site.name} » ?\n\n` +
        'Le site sort des listes mais reste attaché à son historique. Motif (facultatif) :',
    );
    if (motif === null) return;
    try {
      await api.archiveSite(site.id, motif || undefined);
      navigate('/parc');
    } catch (e) {
      // Le refus le plus courant est attendu : un site qui porte encore des
      // machines ne s'archive pas. Le message du serveur dit quoi faire.
      setErreur(e instanceof ApiError ? e.message : 'Archivage impossible.');
    }
  };

  if (erreur && !site) {
    return (
      <>
        <div className="message erreur">{erreur}</div>
        <Link to="/parc">← Retour au parc</Link>
      </>
    );
  }

  if (!site) {
    return <div className="vide">Chargement…</div>;
  }

  const adresse = [site.addressLine1, [site.postalCode, site.city].filter(Boolean).join(' ')]
    .filter((x) => x && x.trim() !== '')
    .join(', ');
  const aucuneConsigne = !site.accessNotes && !site.openingHours && !site.safetyNotes;

  return (
    <>
      <div className="fil-ariane">
        <Link to="/parc">← Parc</Link>
      </div>

      <div className="entete-page">
        <div>
          <h1>{site.name}</h1>
          <p>
            <Link to={`/tiers/${site.customerId}`}>{site.customerName}</Link>
            {site.city && <> · {site.city}</>}
            {site.code && <> · {site.code}</>}
          </p>
        </div>
        <div className="actions">
          {site.status !== 'ACTIVE' && <span className="etat attente">{site.status}</span>}
          <button className="secondaire" onClick={() => setFormulaireOuvert(true)}>
            Corriger
          </button>
          <button className="secondaire danger-discret" onClick={() => void archiver()}>
            Archiver
          </button>
        </div>
      </div>

      {erreur && <div className="message erreur">{erreur}</div>}

      <div className="carte">
        <h2>Avant de partir</h2>
        {aucuneConsigne ? (
          <div className="vide">
            Aucune consigne. Code d’accès, horaires d’ouverture, EPI obligatoires : c’est ce
            qu’un technicien doit savoir avant de prendre la route.
          </div>
        ) : (
          <div className="carte-corps">
            <dl className="proprietes">
              <dt>Accès</dt>
              <dd className="consigne">{site.accessNotes ?? <span className="aide">—</span>}</dd>
              <dt>Horaires</dt>
              <dd className="consigne">{site.openingHours ?? <span className="aide">—</span>}</dd>
              <dt>Sécurité</dt>
              <dd className="consigne">{site.safetyNotes ?? <span className="aide">—</span>}</dd>
            </dl>
          </div>
        )}
      </div>

      <div className="carte">
        <h2>Localisation</h2>
        <div className="carte-corps">
          <dl className="proprietes">
            <dt>Adresse</dt>
            <dd>{adresse || <span className="aide">—</span>}</dd>
            <dt>Zone</dt>
            <dd>{site.zone ?? <span className="aide">—</span>}</dd>
            <dt>Client</dt>
            <dd>
              <Link to={`/tiers/${site.customerId}`}>{site.customerName}</Link>
            </dd>
          </dl>
        </div>
      </div>

      <div className="carte">
        <h2>Machines ({machines.length})</h2>
        {machines.length === 0 ? (
          <div className="vide">Aucune machine sur ce site.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Machine</th>
                <th style={{ width: 140 }}>Famille</th>
                <th>Emplacement</th>
                <th style={{ width: 140 }}>QR</th>
                <th style={{ width: 130 }}>État</th>
              </tr>
            </thead>
            <tbody>
              {machines.map((m) => {
                const etat = STATUT_MACHINE[m.status];
                return (
                  <tr key={m.id}>
                    <td>
                      <Link className="lien-fort" to={`/machines/${m.id}`}>
                        {m.label ?? m.model ?? 'Sans désignation'}
                      </Link>
                      <div className="aide">
                        {m.brandName ?? '—'}
                        {m.model && <> · {m.model}</>}
                      </div>
                    </td>
                    <td>{m.assetType}</td>
                    <td>{m.location ?? <span className="aide">—</span>}</td>
                    <td>
                      <code className="qr">{m.qrCode}</code>
                    </td>
                    <td>
                      <span className={`etat ${etat.classe}`}>{etat.texte}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Plan d'accès, photo de l'entrée, permis de travail : des pièces qu'on
          rattache au site plutôt qu'à chaque machine. */}
      <PiecesJointes ownerType="site" ownerId={site.id} />

      {formulaireOuvert && (
        <FormulaireSite
          site={site}
          onFermer={() => setFormulaireOuvert(false)}
          onEnregistre={() => void charger()}
        />
      )}
    </>
  );
}
